import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import { resources, type AppLanguage } from './resources';

const STORAGE_KEY = 'appLanguage';
const USER_SELECTED_STORAGE_KEY = 'appLanguageUserSelected';
const FALLBACK_LANGUAGE: AppLanguage = 'pt-BR';

const normalizeLanguage = (language?: string | null): AppLanguage => {
  if (!language) {
    return FALLBACK_LANGUAGE;
  }

  if (language.toLowerCase().startsWith('pt')) {
    return 'pt-BR';
  }

  return 'en';
};

const readStoredLanguage = (): AppLanguage | null => {
  try {
    if (typeof window === 'undefined') {
      return null;
    }

    const wasUserSelected = localStorage.getItem(USER_SELECTED_STORAGE_KEY) === '1';
    if (!wasUserSelected) {
      return null;
    }

    const storedLanguage = localStorage.getItem(STORAGE_KEY);
    if (!storedLanguage) {
      return null;
    }

    return normalizeLanguage(storedLanguage);
  } catch {
    return null;
  }
};

const persistLanguage = (language: AppLanguage) => {
  try {
    if (typeof window === 'undefined') {
      return;
    }

    localStorage.setItem(STORAGE_KEY, language);
  } catch {
    // Ignore storage failures in restricted environments.
  }
};

export const markLanguageAsUserSelected = () => {
  try {
    if (typeof window === 'undefined') {
      return;
    }
    localStorage.setItem(USER_SELECTED_STORAGE_KEY, '1');
  } catch {
    // Ignore storage failures in restricted environments.
  }
};

const getInitialLanguage = (): AppLanguage => {
  const storedLanguage = readStoredLanguage();
  if (storedLanguage) {
    return storedLanguage;
  }

  // App default is always Portuguese (Brazil).
  return FALLBACK_LANGUAGE;
};

const initialLanguage = getInitialLanguage();
persistLanguage(initialLanguage);

void i18n.use(initReactI18next).init({
  resources,
  lng: initialLanguage,
  fallbackLng: FALLBACK_LANGUAGE,
  supportedLngs: ['pt-BR', 'en'],
  interpolation: { escapeValue: false },
});

if (typeof document !== 'undefined') {
  document.documentElement.lang = initialLanguage;
}

i18n.on('languageChanged', (language) => {
  const normalizedLanguage = normalizeLanguage(language);
  persistLanguage(normalizedLanguage);
  if (typeof document !== 'undefined') {
    document.documentElement.lang = normalizedLanguage;
  }
});

export { i18n };
